import * as THREE from "three";
import type { Rng } from "../rng";
import { flat, shade, Tone } from "../materials";
import { blobGeometry, mesh } from "../geometry";
import { Slot, slotNormal } from "../palette";
import { TORCH } from "../config";

const SKIN = 0xdedad1;
const TUNIC = 0xaba89d;
const TRIM = 0xefeee9;
const TROUSERS = 0x615f59;
const BOOTS = 0x2c2926;
const HAIR = 0x3b3733;
const CLOAK = 0x8c8b83;
const BELT = 0x453f38;
const IRON = 0x9b9993;

const TAU = Math.PI * 2;
const WALK_SPEED = 3.1;

interface Gait {
  strideLength: number;
  hip: number;
  kneeSwing: number;
  kneeStance: number;
  arm: number;
  elbowBase: number;
  elbowSwing: number;
  lean: number;
  bob: number;
  twist: number;
}

const WALK: Gait = {
  strideLength: 1.5,
  hip: 0.5,
  kneeSwing: 0.95,
  kneeStance: 0.09,
  arm: 0.44,
  elbowBase: -0.28,
  elbowSwing: 0.5,
  lean: 0.05,
  bob: 0.032,
  twist: 0.075,
};

const RUN: Gait = {
  strideLength: 2.5,
  hip: 0.82,
  kneeSwing: 1.65,
  kneeStance: 0.42,
  arm: 0.92,
  elbowBase: -1.05,
  elbowSwing: 0.85,
  lean: 0.22,
  bob: 0.072,
  twist: 0.16,
};

const blendGait = (t: number): Gait => ({
  strideLength: WALK.strideLength + (RUN.strideLength - WALK.strideLength) * t,
  hip: WALK.hip + (RUN.hip - WALK.hip) * t,
  kneeSwing: WALK.kneeSwing + (RUN.kneeSwing - WALK.kneeSwing) * t,
  kneeStance: WALK.kneeStance + (RUN.kneeStance - WALK.kneeStance) * t,
  arm: WALK.arm + (RUN.arm - WALK.arm) * t,
  elbowBase: WALK.elbowBase + (RUN.elbowBase - WALK.elbowBase) * t,
  elbowSwing: WALK.elbowSwing + (RUN.elbowSwing - WALK.elbowSwing) * t,
  lean: WALK.lean + (RUN.lean - WALK.lean) * t,
  bob: WALK.bob + (RUN.bob - WALK.bob) * t,
  twist: WALK.twist + (RUN.twist - WALK.twist) * t,
});

export interface Character {
  root: THREE.Group;
  footOffset: number;
  update(delta: number, speed: number, time: number): void;
  setTorchCarried(on: boolean): void;
  readonly torchCarried: boolean;
  torchGlow(daylight: number): void;
  step(strength: number): void;
  setGait(blend: number): void;
  setPose(phase: number, intensity: number, time: number): void;
}

const prism = (
  radiusTop: number,
  radiusBottom: number,
  height: number,
  sides: number,
  tone: number,
): THREE.Mesh => mesh(new THREE.CylinderGeometry(radiusTop, radiusBottom, height, sides), flat(tone));

const box = (w: number, h: number, d: number, tone: number): THREE.Mesh =>
  mesh(new THREE.BoxGeometry(w, h, d), flat(tone));

function joint(parent: THREE.Object3D, x: number, y: number, z: number): THREE.Group {
  const pivot = new THREE.Group();
  pivot.position.set(x, y, z);
  parent.add(pivot);
  return pivot;
}

interface Leg {
  hip: THREE.Group;
  knee: THREE.Group;
  ankle: THREE.Group;
}

interface Arm {
  shoulder: THREE.Group;
  elbow: THREE.Group;
}

export function buildCharacter(rng: Rng, height = 1.72): Character {
  const scale = height / 1.72;
  const root = new THREE.Group();
  const body = new THREE.Group();
  body.scale.setScalar(scale);
  root.add(body);

  const hipY = 0.87;
  const shoulderY = 1.38;

  const pelvis = prism(0.16, 0.15, 0.2, 8, TROUSERS);
  pelvis.position.y = hipY + 0.02;
  body.add(pelvis);

  const torso = prism(0.185, 0.155, 0.5, 8, TUNIC);
  torso.position.y = hipY + 0.28;
  body.add(torso);

  const hem = prism(0.2, 0.21, 0.09, 8, shade(TUNIC, -0.12));
  hem.position.y = hipY + 0.06;
  body.add(hem);

  const belt = prism(0.168, 0.168, 0.075, 8, BELT);
  belt.position.y = hipY + 0.19;
  body.add(belt);

  const buckle = box(0.06, 0.05, 0.03, IRON);
  buckle.position.set(0, hipY + 0.19, 0.16);
  body.add(buckle);

  const collar = prism(0.12, 0.175, 0.1, 8, TRIM);
  collar.position.y = shoulderY + 0.02;
  body.add(collar);

  const neck = prism(0.055, 0.06, 0.09, 6, shade(SKIN, -0.12));
  neck.position.y = shoulderY + 0.1;
  body.add(neck);

  const head = box(0.2, 0.22, 0.21, SKIN);
  head.position.y = shoulderY + 0.28;
  body.add(head);

  const jaw = box(0.17, 0.07, 0.18, shade(SKIN, -0.06));
  jaw.position.y = shoulderY + 0.18;
  body.add(jaw);

  const nose = box(0.045, 0.05, 0.05, shade(SKIN, -0.1));
  nose.position.set(0, shoulderY + 0.26, 0.115);
  body.add(nose);

  const brow = box(0.19, 0.03, 0.03, shade(SKIN, -0.22));
  brow.position.set(0, shoulderY + 0.33, 0.105);
  body.add(brow);

  const hairCap = blobGeometry(0.125, 1, 0.16, rng);
  hairCap.scale(1, 0.85, 1.02);
  const hair = mesh(hairCap, flat(HAIR));
  hair.position.set(0, shoulderY + 0.36, -0.01);
  body.add(hair);

  for (let i = 0; i < 4; i++) {
    const tuft = blobGeometry(rng.range(0.05, 0.085), 0, 0.3, rng);
    const strand = mesh(tuft, flat(shade(HAIR, rng.range(-0.05, 0.08))));
    strand.position.set(
      rng.range(-0.1, 0.1),
      shoulderY + rng.range(0.3, 0.38),
      rng.range(-0.12, 0.06),
    );
    body.add(strand);
  }

  const cloak = mesh(
    new THREE.CylinderGeometry(0.11, 0.29, 0.62, 8, 1, true, Math.PI * 0.62, Math.PI * 0.76),
    flat(CLOAK),
  );
  cloak.position.set(0, hipY + 0.28, -0.03);
  body.add(cloak);

  const satchel = box(0.16, 0.16, 0.09, BELT);
  satchel.position.set(0.19, hipY + 0.12, -0.05);
  satchel.rotation.z = 0.12;
  body.add(satchel);

  const strap = box(0.035, 0.42, 0.03, BELT);
  strap.position.set(0.1, hipY + 0.34, 0.02);
  strap.rotation.z = -0.42;
  body.add(strap);

  const scabbard = box(0.05, 0.5, 0.07, shade(BELT, -0.18));
  scabbard.position.set(-0.16, hipY + 0.24, -0.14);
  scabbard.rotation.z = 0.34;
  body.add(scabbard);

  const hilt = box(0.035, 0.1, 0.035, IRON);
  hilt.position.set(-0.24, hipY + 0.5, -0.16);
  hilt.rotation.z = 0.34;
  body.add(hilt);

  const guard = box(0.12, 0.025, 0.03, shade(IRON, -0.15));
  guard.position.set(-0.225, hipY + 0.45, -0.16);
  guard.rotation.z = 0.34;
  body.add(guard);

  const buildArm = (side: number): Arm => {
    const shoulder = joint(body, side * 0.2, shoulderY - 0.03, 0);
    const upper = prism(0.055, 0.05, 0.26, 6, TUNIC);
    upper.position.y = -0.13;
    shoulder.add(upper);

    const elbow = joint(shoulder, 0, -0.26, 0);
    const fore = prism(0.045, 0.04, 0.24, 6, shade(TUNIC, -0.08));
    fore.position.y = -0.12;
    elbow.add(fore);

    const hand = prism(0.05, 0.045, 0.08, 6, SKIN);
    hand.position.y = -0.27;
    elbow.add(hand);

    return { shoulder, elbow };
  };

  const buildLeg = (side: number): Leg => {
    const hip = joint(body, side * 0.095, hipY - 0.08, 0);
    const thigh = prism(0.078, 0.066, 0.4, 6, TROUSERS);
    thigh.position.y = -0.2;
    hip.add(thigh);

    const knee = joint(hip, 0, -0.4, 0);
    const shin = prism(0.062, 0.05, 0.36, 6, shade(TROUSERS, -0.06));
    shin.position.y = -0.18;
    knee.add(shin);

    const ankle = joint(knee, 0, -0.36, 0);
    const boot = box(0.12, 0.1, 0.21, BOOTS);
    boot.position.set(0, -0.05, 0.035);
    ankle.add(boot);

    const sole = box(0.13, 0.03, 0.23, shade(BOOTS, -0.4));
    sole.position.set(0, -0.1, 0.04);
    ankle.add(sole);

    return { hip, knee, ankle };
  };

  const armLeft = buildArm(-1);
  const armRight = buildArm(1);
  const legLeft = buildLeg(-1);
  const legRight = buildLeg(1);

  const torchFlame = new THREE.MeshBasicMaterial({ color: 0xffffff });
  const torch = new THREE.Group();
  const shaft = mesh(new THREE.CylinderGeometry(0.021, 0.026, 0.5, 5), flat(BELT));
  shaft.position.y = 0.02;
  torch.add(shaft);
  const torchHead = mesh(new THREE.CylinderGeometry(0.05, 0.036, 0.13, 6), flat(Tone.ink));
  torchHead.position.y = 0.26;
  torch.add(torchHead);
  const flame = new THREE.Mesh(new THREE.ConeGeometry(0.105, 0.34, 6, 1, true), torchFlame);
  flame.position.y = 0.42;
  flame.layers.enable(1);
  flame.userData.slot = slotNormal(Slot.fire);
  flame.userData.variant = 0.2;
  torch.add(flame);
  const torchLight = new THREE.PointLight(0xffa04a, 0, TORCH.lightDistance, 1.25);
  torchLight.position.y = 0.56;
  torch.add(torchLight);
  torch.position.set(0, -0.3, 0.06);
  torch.visible = false;
  armRight.elbow.add(torch);

  const upright = new THREE.Quaternion();
  const parentWorld = new THREE.Quaternion();

  let torchBlend = 0;
  let torchTarget = 0;
  let torchSeed = 0;

  let phase = 0;
  let stepLift = 0;
  let gaitForPose: Gait = WALK;

  const poseLeg = (leg: Leg, theta: number, intensity: number, gait: Gait): void => {
    const hipAngle = -Math.sin(theta) * gait.hip * intensity;
    const swing = Math.max(0, Math.cos(theta));
    const kneeAngle = (gait.kneeStance + Math.pow(swing, 1.15) * gait.kneeSwing) * intensity;

    leg.hip.rotation.x = hipAngle;
    leg.knee.rotation.x = kneeAngle;
    leg.ankle.rotation.x = -(hipAngle + kneeAngle) * 0.72 - swing * 0.18 * intensity;
  };

  const applyPose = (theta: number, intensity: number, gait: Gait, time: number): void => {
    poseLeg(legLeft, theta, intensity, gait);
    poseLeg(legRight, theta + Math.PI, intensity, gait);

    const armAngle = Math.sin(theta) * gait.arm * intensity;
    armLeft.shoulder.rotation.x = armAngle;
    armRight.shoulder.rotation.x = -armAngle;
    if (torchBlend > 0.001) {
      armRight.shoulder.rotation.x += (-0.95 - armRight.shoulder.rotation.x) * torchBlend;
      armRight.shoulder.rotation.z += (0.22 - armRight.shoulder.rotation.z) * torchBlend;
      armRight.elbow.rotation.x += (-0.5 - armRight.elbow.rotation.x) * torchBlend;
    }
    armLeft.shoulder.rotation.z = -0.1;
    armRight.shoulder.rotation.z = 0.1;
    armLeft.elbow.rotation.x = gait.elbowBase - Math.max(0, Math.sin(theta)) * gait.elbowSwing * intensity;
    armRight.elbow.rotation.x = gait.elbowBase - Math.max(0, -Math.sin(theta)) * gait.elbowSwing * intensity;

    if (stepLift > 0.001) {
      const leading = Math.sin(theta) >= Math.sin(theta + Math.PI) ? legLeft : legRight;
      leading.knee.rotation.x += stepLift * 1.3;
      leading.hip.rotation.x -= stepLift * 0.55;
      leading.ankle.rotation.x += stepLift * 0.4;
    }

    const bob = Math.abs(Math.cos(theta)) * gait.bob * intensity;
    body.position.y = bob + stepLift * 0.035;
    body.rotation.x = intensity * gait.lean;
    body.rotation.z = Math.sin(theta) * 0.025 * intensity;

    pelvis.rotation.y = -Math.sin(theta) * gait.twist * intensity;
    torso.rotation.y = Math.sin(theta) * gait.twist * 0.75 * intensity;
    collar.rotation.y = torso.rotation.y;
    head.rotation.y = Math.sin(time * 0.4) * 0.07 * (1 - intensity) - torso.rotation.y * 0.6;
    head.rotation.z = Math.cos(time * 0.33) * 0.035 * (1 - intensity);

    const breathe = 1 + Math.sin(time * 1.5) * 0.006 * (1 - intensity);
    torso.scale.set(breathe, 1, breathe);
  };

  const update = (delta: number, speed: number, time: number): void => {
    stepLift *= Math.exp(-delta / 0.1);
    torchBlend += (torchTarget - torchBlend) * (1 - Math.exp(-delta / 0.25));
    if (Math.abs(torchTarget - torchBlend) < 0.002) torchBlend = torchTarget;
    torchSeed = time;

    if (torch.visible && torch.parent) {
      torch.parent.updateWorldMatrix(true, false);
      torch.parent.getWorldQuaternion(parentWorld);
      torch.quaternion.copy(upright.copy(parentWorld).invert());
    }
    if (stepLift < 0.002) stepLift = 0;

    const intensity = Math.min(1, speed / WALK_SPEED);
    const runBlend = Math.min(1, Math.max(0, (speed - WALK_SPEED) / (WALK_SPEED * 0.55)));
    const gait = blendGait(runBlend);
    phase += (speed / gait.strideLength) * TAU * delta;
    if (phase > TAU) phase -= TAU;
    applyPose(phase, intensity, gait, time);
  };

  root.updateMatrixWorld(true);
  const footOffset = -new THREE.Box3().setFromObject(root).min.y;

  return {
    root,
    footOffset,
    update,
    get torchCarried(): boolean {
      return torchTarget > 0.5;
    },
    setTorchCarried: (on: boolean): void => {
      torchTarget = on ? 1 : 0;
    },
    torchGlow: (daylight: number): void => {
      const span = Math.max(0.01, TORCH.carryBelow - TORCH.fullBelow);
      const raw = Math.min(1, Math.max(0, (TORCH.carryBelow - daylight) / span));
      // a floor as soon as it is drawn, so it actually throws light on the
      // ground while the sun is still up rather than only once it is dark
      const night = 0.45 + 0.55 * raw;
      const base = TORCH.lightDay + (TORCH.lightNight - TORCH.lightDay) * night;
      const flicker =
        0.88 + Math.sin(torchSeed * 13.3) * 0.07 + Math.sin(torchSeed * 27.1) * 0.05;
      torchLight.intensity = base * flicker * torchBlend;
      torchFlame.opacity = 1;
      flame.scale.set(
        flicker,
        1 + (flicker - 1) * 2.4,
        flicker,
      );
      torch.visible = torchBlend > 0.02;
    },
    step: (strength: number): void => {
      stepLift = Math.min(1, Math.max(stepLift, strength));
    },
    setGait: (blend: number): void => {
      gaitForPose = blendGait(blend);
    },
    setPose: (theta: number, intensity: number, time: number): void => {
      applyPose(theta, intensity, gaitForPose, time);
    },
  };
}
